import React, { useState, useEffect, useRef } from 'react';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import AddIcon from '@mui/icons-material/Add';
import { showAlert } from '../../../functions/show_alert';

export default function WizardSelectPeopleDetailsForm({ principleData, actualPrinciplesData, onDataChange, initialData }) {
    const [people, setPeople] = useState(initialData.people || [{ name: '', image: null, fileName: null }]);
    const [showGivenToSelected, setShowGivenToSelected] = useState(initialData.showGivenToSelected || false);
    const [showGivesToSelected, setShowGivesToSelected] = useState(initialData.showGivesToSelected || false);
    const onDataChangeRef = useRef(onDataChange);

    // Update ref when callback changes
    useEffect(() => {
        onDataChangeRef.current = onDataChange;
    }, [onDataChange]);

    // Only call onDataChange when actual data changes, not when callback changes
    useEffect(() => {
        if (onDataChangeRef.current) {
            onDataChangeRef.current({ 
                people,
                showGivenToSelected,
                showGivesToSelected
            });
        }
    }, [people, showGivenToSelected, showGivesToSelected]);

    const handleNameChange = (index, name) => {
        const updatedPeople = [...people];
        updatedPeople[index] = { ...updatedPeople[index], name };
        setPeople(updatedPeople);
    };

    const compressImage = (file, maxWidth = 400, maxHeight = 400, maxSizeKB = 300) => {
        return new Promise((resolve, reject) => {
            // For PDF files, check size and reject if too large
            if (file.type === 'application/pdf') {
                if (file.size > maxSizeKB * 1024) {
                    reject(new Error(`PDF file size must be less than ${maxSizeKB}KB`));
                    return;
                }
                const reader = new FileReader();
                reader.onloadend = () => {
                    const dataUrl = reader.result;
                    const base64Match = dataUrl.match(/^data:[^;]+;base64,(.+)$/);
                    resolve(base64Match ? base64Match[1] : dataUrl);
                };
                reader.readAsDataURL(file);
                return;
            }

            const reader = new FileReader();
            reader.onloadend = () => {
                const img = new Image();
                img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;

                    // Calculate new dimensions
                    if (width > height) {
                        if (width > maxWidth) {
                            height = (height * maxWidth) / width;
                            width = maxWidth;
                        }
                    } else {
                        if (height > maxHeight) {
                            width = (width * maxHeight) / height;
                            height = maxHeight;
                        }
                    }

                    canvas.width = width;
                    canvas.height = height;

                    const ctx = canvas.getContext('2d');
                    ctx.drawImage(img, 0, 0, width, height);

                    // Try different quality levels to meet size requirement
                    let quality = 0.7;
                    let compressedDataUrl = canvas.toDataURL(file.type, quality);
                    let base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                    let sizeKB = (base64Data.length * 3) / 4 / 1024; // Approximate size in KB

                    // Reduce quality if still too large
                    while (sizeKB > maxSizeKB && quality > 0.3) {
                        quality -= 0.1;
                        compressedDataUrl = canvas.toDataURL(file.type, quality);
                        base64Data = compressedDataUrl.match(/^data:[^;]+;base64,(.+)$/)?.[1] || compressedDataUrl;
                        sizeKB = (base64Data.length * 3) / 4 / 1024;
                    }

                    if (sizeKB > maxSizeKB) {
                        reject(new Error(`Image is too large even after compression (${sizeKB.toFixed(0)}KB). Please use a smaller image.`));
                        return;
                    }

                    resolve(base64Data);
                };
                img.onerror = () => reject(new Error('Failed to load image'));
                img.src = reader.result;
            };
            reader.onerror = () => reject(new Error('Failed to read file'));
            reader.readAsDataURL(file);
        });
    };

    const handleFileUpload = async (index, file) => {
        // Check original file size (before compression)
        if (file.size > 10000000) {
            showAlert(false, 'The file size is more than 10 MB');
            return;
        }

        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
        if (!validTypes.includes(file.type)) {
            showAlert(false, 'Only .jpg, .png, & .pdf files will be accepted');
            return;
        }

        try {
            // Compress image before storing (max 300KB after compression)
            const base64Data = await compressImage(file, 400, 400, 300);
            const updatedPeople = [...people];
            updatedPeople[index] = {
                ...updatedPeople[index],
                image: base64Data,
                fileName: file.name
            };
            setPeople(updatedPeople);
        } catch (error) {
            console.error('Error processing file:', error);
            showAlert(false, error.message || 'Error processing file. Please try again.');
        }
    };

    const handleAddPerson = () => {
        setPeople([...people, { name: '', image: null, fileName: null }]);
    };

    return (
        <div className="wizard-select-people-details-form">
            <h2 className="wizard-step-heading">Select People Details</h2>
            <div className="people-details-container">
                {people.map((person, index) => (
                    <div key={index} className="person-entry">
                        <div className="form-field">
                            <label className="field-label">Name</label>
                            <input
                                type="text"
                                className="form-input"
                                placeholder="Enter name"
                                value={person.name}
                                onChange={(e) => handleNameChange(index, e.target.value)}
                            />
                        </div>

                        <div className="form-field">
                            <label className="field-label">Upload Image</label>
                            <div
                                className={`file-upload-area ${person.fileName ? 'has-file' : ''}`}
                                onClick={() => {
                                    document.getElementById(`people-file-input-${index}`).click();
                                }}
                            >
                                <input
                                    id={`people-file-input-${index}`}
                                    type="file"
                                    style={{ display: 'none' }}
                                    accept=".jpg,.jpeg,.png,.pdf"
                                    onChange={(e) => {
                                        if (e.target.files && e.target.files[0]) {
                                            handleFileUpload(index, e.target.files[0]);
                                        }
                                    }}
                                />
                                <CloudUploadIcon className="upload-icon" />
                                <p className="upload-text">Upload the file here</p>
                                <p className="upload-hint">(Only .jpg, .png, & .pdf files will be accepted)</p>
                                {person.fileName && (
                                    <p className="uploaded-file">{person.fileName}</p>
                                )}
                            </div>
                            {!person.fileName && (
                                <p className="no-files-text">no files uploaded yet</p>
                            )}
                        </div>
                    </div>
                ))}

                <button className="add-person-button" onClick={handleAddPerson}>
                    <AddIcon className="add-person-icon" />
                    <span className="add-person-text">Add another person</span>
                </button>

                <div className="show-options-container">
                    <div 
                        className={`show-option ${showGivenToSelected ? 'selected' : ''}`}
                        onClick={() => {
                            setShowGivenToSelected(true);
                            setShowGivesToSelected(false);
                        }}
                    >
                        <div className="show-option-content">
                            <span className="show-option-label">Show Given To</span>
                            <input
                                type="radio"
                                name="showOption"
                                checked={showGivenToSelected}
                                onChange={() => {
                                    setShowGivenToSelected(true);
                                    setShowGivesToSelected(false);
                                }}
                                className="show-option-radio"
                            />
                        </div>
                    </div>

                    <div 
                        className={`show-option ${showGivesToSelected ? 'selected' : ''}`}
                        onClick={() => {
                            setShowGivesToSelected(true);
                            setShowGivenToSelected(false);
                        }}
                    >
                        <div className="show-option-content">
                            <span className="show-option-label">Show Gives to</span>
                            <input
                                type="radio"
                                name="showOption"
                                checked={showGivesToSelected}
                                onChange={() => {
                                    setShowGivesToSelected(true);
                                    setShowGivenToSelected(false);
                                }}
                                className="show-option-radio"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
